import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { RootState } from '.';
import api from '../api/BlancoClient';
import { sendException } from '../lib/google-analytics';
import { analyzeQrBinary } from '../lib/MyDesignParser';
import { openQRCodeReader, QRSource } from '../lib/QrReader';
import { MyDesign } from '../models/MyDesign';
import { MyDesignQR } from '../models/MyDesignQR';
import { actions as userActions, getFreshToken } from './user';
import { checkErrorType, Progress } from './utils/helper';

type MyDesignErrorType = 'load-failed' | 'unstable-network' | 'unknown';
export type MyDesignUploadErrorType = 'unstable-network' | 'unknown';
export type MyDesignDeleteErrorType = 'no-exist' | 'unstable-network' | 'unknown';

export interface MyDesignState {
  readMyDesigns?: MyDesignQR[];
  invalidated?: boolean;
  isLoading: boolean;
  uploadedMyDesign?: MyDesign;
  uploadStatus?: Progress;
  deleteStatus?: Progress;
  errorType?: MyDesignErrorType;
  uploadErrorType?: MyDesignUploadErrorType;
  deleteErrorType?: MyDesignDeleteErrorType;
}

const initialState: MyDesignState = {
  isLoading: true
};

const slice = createSlice({
  name: 'myDesign',
  initialState,
  reducers: {
    discardMyDesign: state => {
      state.readMyDesigns = undefined;
      state.invalidated = undefined;
      state.uploadStatus = undefined;
      state.deleteStatus = undefined;
    },

    invalidateMyDesign: state => {
      state.invalidated = true;
    },

    readMyDesign: (state, _: PayloadAction<QRSource>) => {
      state.readMyDesigns = undefined;
      state.invalidated = undefined;
      state.uploadStatus = undefined;
    },

    readMyDesignSucceeded: (state, { payload }: PayloadAction<MyDesignQR[]>) => {
      state.readMyDesigns = payload;
      state.invalidated = false;
      state.uploadStatus = undefined;
      state.deleteStatus = undefined;
    },

    getMyDesign: state => {
      state.isLoading = true;
      state.errorType = undefined;
      state.deleteStatus = undefined;
    },

    getMyDesignSucceeded: (state, { payload }: PayloadAction<MyDesign | undefined>) => {
      state.isLoading = false;
      state.uploadedMyDesign = payload;
      state.errorType = undefined;
    },

    getMyDesignFailed: (state, { payload }: PayloadAction<MyDesignErrorType>) => {
      state.isLoading = false;
      state.errorType = payload;
    },

    postMyDesign: state => {
      state.uploadStatus = 'loading';
      state.uploadErrorType = undefined;
    },

    postMyDesignSucceeded: state => {
      state.uploadStatus = 'succeeded';
      state.uploadedMyDesign = undefined;
      state.readMyDesigns = undefined;
      state.invalidated = undefined;
    },

    postMyDesignFailed: (state, { payload }: PayloadAction<MyDesignUploadErrorType>) => {
      state.uploadStatus = 'failed';
      state.uploadErrorType = payload;
    },

    upsertMyDesign: state => {
      state.uploadStatus = 'loading';
      state.uploadErrorType = undefined;
    },

    deleteMyDesign: state => {
      state.deleteStatus = 'loading';
      state.deleteErrorType = undefined;
    },

    deleteMyDesignSucceeded: state => {
      state.deleteStatus = 'succeeded';
      state.uploadedMyDesign = undefined;
      state.readMyDesigns = undefined;
      state.invalidated = undefined;
    },

    deleteMyDesignFailed: (
      state,
      {
        payload
      }: PayloadAction<{ deleteStatus: Progress; deleteErrorType: MyDesignDeleteErrorType }>
    ) => {
      state.deleteStatus = payload.deleteStatus;
      state.deleteErrorType = payload.deleteErrorType;
    },

    clearError: state => {
      if (state.uploadStatus === 'failed') {
        state.uploadStatus = undefined;
        state.uploadErrorType = undefined;
      }
      if (state.deleteStatus === 'failed') {
        state.deleteStatus = undefined;
        state.deleteErrorType = undefined;
      }
    },

    reset: state => {
      state.isLoading = initialState.isLoading;
      state.deleteErrorType = undefined;
      state.deleteStatus = undefined;
      state.errorType = undefined;
      state.invalidated = undefined;
      state.readMyDesigns = undefined;
      state.uploadErrorType = undefined;
      state.uploadStatus = undefined;
      state.uploadedMyDesign = undefined;
    }
  }
});

export default slice.reducer;

const {
  discardMyDesign,
  invalidateMyDesign,
  readMyDesign,
  getMyDesign,
  postMyDesign,
  upsertMyDesign,
  deleteMyDesign,
  clearError,
  reset,
  ...internalActions
} = slice.actions;

export const actions = {
  discardMyDesign,
  invalidateMyDesign,
  readMyDesign,
  getMyDesign,
  postMyDesign,
  upsertMyDesign,
  deleteMyDesign,
  clearError,
  reset
};

const {
  readMyDesignSucceeded,
  getMyDesignSucceeded,
  getMyDesignFailed,
  postMyDesignSucceeded,
  postMyDesignFailed,
  deleteMyDesignSucceeded,
  deleteMyDesignFailed
} = internalActions;

// Saga

export function* myDesignSaga() {
  yield takeEvery(getMyDesign.type, getMyDesignSaga);
  yield takeEvery(upsertMyDesign.type, upsertMyDesignSaga);
  yield takeEvery(postMyDesign.type, postMyDesignSaga);
  yield takeLatest(deleteMyDesign.type, deleteMyDesignSaga);
  yield takeLatest(readMyDesign.type, readMyDesignSaga);
}

function* postMyDesignSaga() {
  try {
    const baid: string = yield call(getFreshToken);
    const myDesignState: MyDesignState = yield select((state: RootState) => state.myDesign);
    const { readMyDesigns } = myDesignState;
    if (!readMyDesigns || readMyDesigns.length === 0) {
      sendException('Trying to upload empty QRs in postMyDesignSaga', true);
    } else {
      const qrTexts = readMyDesigns.map(d => d.qrText);
      yield call(api.uploadDesign, qrTexts, baid);
      yield put(postMyDesignSucceeded());
    }
  } catch (error) {
    console.error(error);
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(postMyDesignFailed('unstable-network'));
    } else {
      yield put(postMyDesignFailed('unknown'));
    }
    sendException('Uploading my design failed');
  }
}

function* upsertMyDesignSaga() {
  try {
    const baid: string = yield call(getFreshToken);
    const myDesignState: MyDesignState = yield select((state: RootState) => state.myDesign);
    const { uploadedMyDesign, readMyDesigns } = myDesignState;

    try {
      if (uploadedMyDesign) {
        yield call(api.deleteDesign, uploadedMyDesign.id, baid);
      }
    } catch (error) {
      if (error.response.data?.code === '1201') {
        // 既にそのマイデザインが存在しなかった場合は何もしない。
      } else {
        throw error;
      }
    }

    if (!readMyDesigns || readMyDesigns.length === 0) {
      sendException('Trying to upload empty QRs in upsertMyDesignSaga', true);
    } else {
      const qrTexts = readMyDesigns.map(d => d.qrText);
      yield call(api.uploadDesign, qrTexts, baid);
      yield put(postMyDesignSucceeded());
    }
  } catch (error) {
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(postMyDesignFailed('unstable-network'));
    } else {
      yield put(postMyDesignFailed('unknown'));
    }
  }
}

function* deleteMyDesignSaga() {
  try {
    const baid: string = yield call(getFreshToken);
    const myDesignState: MyDesignState = yield select((state: RootState) => state.myDesign);
    const { uploadedMyDesign } = myDesignState;
    if (uploadedMyDesign) {
      yield call(api.deleteDesign, uploadedMyDesign.id, baid);
    }
    yield put(deleteMyDesignSucceeded());
  } catch (error) {
    console.error(error);
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(
        deleteMyDesignFailed({
          deleteStatus: 'failed',
          deleteErrorType: 'unstable-network'
        })
      );
    } else {
      let deleteErrorType: MyDesignDeleteErrorType;
      if (error.response.data?.code === '1201') {
        // 既にそのマイデザインが存在しなかった場合
        deleteErrorType = 'no-exist';
      } else {
        deleteErrorType = 'unknown';
      }
      yield put(deleteMyDesignFailed({ deleteStatus: 'failed', deleteErrorType }));
      if (deleteErrorType === 'no-exist') {
        yield put(getMyDesign());
      }
    }
  }
}

function* getMyDesignSaga() {
  try {
    const baid: string = yield call(getFreshToken);
    const { legacy_designs } = yield call(api.getDesigns, baid);

    if (legacy_designs.length === 0) {
      yield put(getMyDesignSucceeded(undefined));
    } else {
      const { id, data } = legacy_designs[0];
      const qrTexts = data.split(',');
      if (qrTexts.length !== 1 && qrTexts.length !== 4) {
        yield put(getMyDesignFailed('load-failed'));
      }
      const parseResults = qrTexts.map(analyzeQrBinary);
      if (parseResults.some((qr: MyDesignQR | undefined) => !qr)) {
        sendException('Parsing failed because of invalid QR format');
        yield put(getMyDesignFailed('load-failed'));
        return;
      }
      const myDesignQRs = parseResults as MyDesignQR[];
      const first = myDesignQRs[0];
      let colorData: Uint8Array | Uint8Array[];
      if (myDesignQRs.length === 4) {
        colorData = myDesignQRs.map(d => d.colorData);
      } else {
        colorData = first.colorData;
      }
      const design: MyDesign = {
        id,
        title: first.title || '',
        creator: first.creator || '',
        usage: first.usage,
        designType: first.designType,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        paletteColors: first.paletteColors!,
        colorData,
        qrTexts
      };
      yield put(getMyDesignSucceeded(design));
    }
  } catch (error) {
    console.error(error);
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(getMyDesignFailed('unstable-network'));
    } else {
      yield put(getMyDesignFailed('load-failed'));
    }
  }
}

function* readMyDesignSaga(action: { type: typeof readMyDesign.type; payload: QRSource }) {
  const myDesigns: MyDesignQR[] = yield call(readMyDesignQRs, action.payload);
  yield put(readMyDesignSucceeded(myDesigns));
}

async function readMyDesignQRs(source: QRSource) {
  return new Promise<MyDesignQR[]>(resolve => {
    openQRCodeReader(source, result => {
      resolve(result);
    });
  });
}
